// src/components/ChunkErrorFallback.jsx
import { useLayoutEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

const RELOAD_KEY = 'last-chunk-error-reload';
const RELOAD_COOLDOWN_MS = 10000; // 10 s — prevents infinite reload loop

export default function ChunkErrorFallback() {
  // Start hidden — useLayoutEffect fires before the browser paints,
  // so if a reload is warranted the user never sees this component at all.
  const [showManualError, setShowManualError] = useState(false);

  useLayoutEffect(() => {
    try {
      const last = sessionStorage.getItem(RELOAD_KEY);
      const now = Date.now();

      if (!last || now - Number(last) > RELOAD_COOLDOWN_MS) {
        // Reload is safe — fire it before the browser gets a chance to paint
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
        return; // keep showManualError false — page is reloading
      }
    } catch {
      // sessionStorage blocked (e.g. private mode) — fall through
    }

    // Cooldown active: auto-reload already fired recently and didn't fix it.
    // Show the manual error UI as a last resort.
    setShowManualError(true);
  }, []);

  // Render nothing while the reload is in flight (before first paint)
  if (!showManualError) return null;

  const handleManualReload = () => {
    try {
      // Force past the cooldown so the user's explicit click always works
      sessionStorage.setItem(RELOAD_KEY, String(Date.now() - RELOAD_COOLDOWN_MS - 1));
    } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 480, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          We could not load this page
        </Typography>

        <Typography variant="body1">
          There was a problem loading the latest version of the app. This can
          happen after an update or due to a weak connection.
          <br />
          Please reload the app and try again. If it continues, let management
          know.
        </Typography>

        <Button variant="contained" onClick={handleManualReload}>
          Reload App
        </Button>
      </Stack>
    </Box>
  );
}
